import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import { useForm } from "react-hook-form";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import WSTextField from "../../../Components/TextBox/WSTextBox";
import WSPhone from "../../../Components/Phone/WSPhone";
import { customValidation, validations, callAPI, Method } from "../../../commonFunctions/CommonFunctions";
import WSTextAreaField from "../../../Components/WSTextArea/WSTextArea";
import { useSnackbar } from "../../../Components/Snackbar/SnackBarContext";
import ConfirmationPopup from "../../../Components/Confirmation/Confirmation";

const Supplier = () => {
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [selectedId, setSelectedId] = useState<string | number>(0);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const { showMessage } = useSnackbar();
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();

  const location = useLocation();
  const from = location.state?.from;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      SupplierName: "",
      ContactPerson: "",
      Phone: "",
      altphone: "",
      Email: "",
      GSTNumber: "",
      Address: "",
      IsActive: true,
    },
  });


  useEffect(() => {


    if (!id || id === "undefined" || id === "null" || id.trim() === "") {
      setIsEditMode(false);
      reset();
      return;
    }


    setIsEditMode(true);
    fetchSupplierData(id);
  }, [id, showMessage, reset]);

  const fetchSupplierData = async (supplierId: string) => {
    try {
      setLoading(true);

      const response = await callAPI(`suppliers/${supplierId}`, Method.GET);

      if (response?.success && response.data) {
        const supplierData = response.data;

        reset({
          SupplierName: supplierData.SupplierName || "",
          ContactPerson: supplierData.ContactPerson || "",
          Phone: supplierData.Phone || "",
          altphone: supplierData.altphone || "",
          Email: supplierData.Email || "",
          GSTNumber: supplierData.GSTNumber || "",
          Address: supplierData.Address || "",
          IsActive: supplierData.IsActive !== false,
        });
      } else {
        console.error("❌ No data in response:", response);
        showMessage(response?.message || `Supplier ${supplierId} not found`, "error");
        setIsEditMode(false);
      }
    } catch (error: any) {
      console.error("❌ Fetch error:", error.response?.data || error.message);
      showMessage("Failed to load supplier data", "error");
      setIsEditMode(false);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      setLoading(true);

      const requestBody = {
        SupplierName: data.SupplierName?.trim(),
        ContactPerson: data.ContactPerson?.trim() || "",
        Phone: data.Phone || "",
        altphone: data.altphone || "",
        Email: data.Email?.trim() || "",
        Address: data.Address?.trim() || "",
        GSTNumber: data.GSTNumber?.trim() || "",
        IsActive: data.IsActive || true,
      };

      console.log('boyd',requestBody)

      let response;
      if (isEditMode && id) {
        response = await callAPI(`suppliers/${id}`, Method.PUT, requestBody);
      } else {
        response = await callAPI("suppliers/create", Method.POST, requestBody);
      }

      if (response?.success) {
        showMessage(
          isEditMode ? "Supplier updated successfully!" : "Supplier created successfully!",
          "success"
        );

        if (response?.success) {
          if (from === "itemmaster") {
            navigate("/itemmaster");
          } else if (from === "adjustment") {
            navigate("/adjustment");
          }
          else if (from === "verification") {
            navigate("/verification");
          }
          else if (from === "stock-return") {
            navigate("/stock-return");
          }
          else {
            navigate("/supplier/list");
          }
        }
      } else {
        showMessage(response?.data?.detail || "Operation failed", "error");
      }
    } catch (error: any) {
      console.error("❌ Submit error:", error);
      showMessage(error.response?.data?.detail || "Operation failed", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2, background: "#f0f0f0", minHeight: "100vh" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box display="flex" alignItems="center" gap={2}>

          <Typography sx={{ fontSize: "20px", fontWeight: 600 }}>
            {isEditMode ? "Edit Supplier" : "Supplier Management"}
          </Typography>
        </Box>
        <Box>
          <IconButton sx={{ background: "#000", color: "#fff", mr: 1 }}>
            <EditIcon fontSize="small" />
          </IconButton>
          { }
        </Box>

      </Box>

      { }
      { }

      { }
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          background: "#fafafa",
          position: "relative"
        }}
      >
        {loading && (
          <Box sx={{
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(255,255,255,0.9)",
            display: "flex", alignItems: "center", justifyContent: "center",
            zIndex: 10, borderRadius: "16px"
          }}>
            <CircularProgress />
          </Box>
        )}

        { }
        <Box display="flex" justifyContent="space-between" mb={7}>
          <Typography sx={{ fontSize: "18px", fontWeight: 500 }}>
            {isEditMode ? "Supplier Details" : "Supplier Details"}
          </Typography>
          <Tooltip title={isEditMode ? "Update Supplier" : "Create Supplier"} arrow>
            <IconButton
              sx={{ background: "#000", color: "#fff" }}
              onClick={handleSubmit(onSubmit)}
              disabled={loading}
            >
              <SaveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <form noValidate>
          <Grid container rowSpacing={8} columnSpacing={5}>
            <Grid item xs={4}>
              <WSTextField
                name="SupplierName"
                label="Supplier Name"
                id="SupplierName"
                control={control}
                rules={{ required: "Supplier name is required" }}
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="ContactPerson"
                label="Contact Person"
                id="ContactPerson"
                control={control}
              />
            </Grid>

            <Grid item xs={4}>
              <WSPhone
                name="Phone"
                label="Phone Number"
                id="Phone"
                control={control}
                
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <WSTextField
                name="Email"
                id="Email"
                label="Email"
                control={control}
                
              />
            </Grid>

            <Grid item xs={4}>
              <WSTextField
                name="GSTNumber"
                label="TRN Number"
                id="GSTNumber"
                control={control}
              />
            </Grid>

            <Grid item xs={4}>
              <WSPhone
                name="altphone"
                label="Alternate Phone Number"
                id="altphone"
                control={control}
              />
            </Grid>

            <Grid item xs={12} md={8}>
              <WSTextAreaField
                name="Address"
                label="Address"
                id="Address"
                control={control}
              />
            </Grid>
          </Grid>
        </form>
      </Paper>
      <ConfirmationPopup
        type="DELETE"
        apiUrl="suppliers"
        id={selectedId}
        confirmMessage="Are you sure you want to delete this supplier?"
        open={isPopupOpen}
        onClose={() => setPopupOpen(false)}
        onConfirm={async (data) => {
          if (!selectedId) return;

          if (data?.success === true) {

            showMessage("supplier deleted successfully!", "success");
            navigate("/supplier/list");
          } else {
            showMessage(data?.detail, "error");
          }

          setPopupOpen(false);
        }}
      />
    </Box>
  );
};

export default Supplier;
